import { Folderish } from "client_legacy/Documents/SharePoint/models";
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";

export type FolderNavigationStackItem = {
	readonly id: number | null;
	readonly name: string;
	readonly viewId: string;
	readonly folder: Folderish;
};

export type FolderNavigationStack = {
	readonly current: FolderNavigationStackItem | undefined;
	readonly items: readonly FolderNavigationStackItem[];
	reset(): void;
	set: Dispatch<SetStateAction<readonly FolderNavigationStackItem[]>>;
	navigateForwardTo(item: FolderNavigationStackItem): void;
	navigateBackTo(folderId: number | null): void;
};

export function useFolderNavigationStack(): FolderNavigationStack {
	const [items, setItems] = useState<readonly FolderNavigationStackItem[]>([]);

	const reset = useCallback(
		(): void => {
			setItems([]);
		},
		[]
	);

	const navigateForwardTo = useCallback(
		(item: FolderNavigationStackItem): void => {
			setItems(
				(current) => {
					const copy: FolderNavigationStackItem[] = [...current];

					if (item.folder.isRoot) {
						if (copy.length !== 0) {
							throw new Error("Cannot navigate to a root folder while the stack is not empty");
						}
					} else {
						if (copy.length === 0) {
							throw new Error("Cannot navigate to a none root folder while the stack is empty");
						}

						// We cannot check for parent id matching because in e.g. Shared with me this is known
						// to not be the case
					}

					copy.push(item);

					return copy;
				}
			);
		},
		[]
	);

	const navigateBackTo = useCallback(
		(folderId: number | null): void => {
			setItems(
				(current) => {
					const copy: FolderNavigationStackItem[] = [...current];

					const index: number = copy.findIndex(x => x.id === folderId);

					if (index === -1) {
						throw new Error("Folder is not present on the stack");
					}

					const length: number = copy.length;
					if (index === length) {
						throw new Error("Can't navigate back to the current folder on the stack");
					}

					copy.splice(index + 1);

					return copy;
				}
			);
		},
		[]
	);

	return useMemo(
		(): FolderNavigationStack => {
			return {
				current: items[items.length - 1],
				items: items,
				reset: reset,
				set: setItems,
				navigateForwardTo,
				navigateBackTo
			};
		},
		[
			items,
			navigateBackTo,
			navigateForwardTo,
			reset
		]
	);
}

export function createStackItem(folder: Folderish): FolderNavigationStackItem {
	return {
		id: folder.id,
		name: folder.name,
		viewId: folder.viewId,
		folder
	};
}